import { styled } from '@porsche-code/kado-styled-system/jsx';
import { ActionGroup, Modal, Spacer } from '@porsche-code/kado-ui';
import {
  PButton,
  PCheckbox,
  PText,
} from '@porsche-design-system/components-react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { SubmitEvent, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import { PersonActionKind, usePersonContext } from '@/context';
import { MeQuery, useMeQuery, useUpdatePersonMutation } from '@/graphql';

const Form = styled('form', {
  base: {
    sm: {
      maxWidth: '50rem',
    },
  },
});

type FormValues = {
  agree: boolean;
};

const isUserDeletionConsentNeeded = (
  consentDateString: string | null | undefined,
): boolean => {
  if (!consentDateString) {
    return true;
  }

  const consentDate = dayjs(consentDateString);
  const oneYearAgo = dayjs().subtract(1, 'year');
  return consentDate.isBefore(oneYearAgo);
};

export const UserDeletionConsent = () => {
  const { t } = useTranslation(NAMESPACES);
  const {
    dispatch: dispatchPerson,
    state: { isAnonymous, acknowledgements },
  } = usePersonContext();
  const { userDeletionConsent } = acknowledgements ?? {};
  const queryClient = useQueryClient();
  const [hasConfirmed, setHasConfirmed] = useState<boolean>(
    () => !isUserDeletionConsentNeeded(userDeletionConsent),
  );

  const { mutateAsync: updatePerson, isPending } = useUpdatePersonMutation({
    onSuccess: (data) => {
      const cacheKey = useMeQuery.getKey({ isAuthenticated: true } as never);
      const cache = queryClient.getQueryData<MeQuery>(cacheKey);

      if (cache?.me && cache.me.__typename === 'Person') {
        queryClient.setQueryData<MeQuery>(cacheKey, {
          me: {
            ...cache.me,
            acknowledgements: {
              ...cache.me?.acknowledgements,
              userDeletionConsent:
                data.updatePerson.acknowledgements?.userDeletionConsent,
            },
          },
        });
      }
    },
  });

  const { formState, handleSubmit, control } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      agree: false,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async () => {
    const date = new Date().toISOString();

    await updatePerson(
      { input: { acknowledgements: { userDeletionConsent: date } } },
      {
        onSuccess: () =>
          dispatchPerson({
            type: PersonActionKind.SetAcknowledgements,
            payload: { userDeletionConsent: date },
          }),
      },
    );
  };

  if (hasConfirmed || isAnonymous) {
    return null;
  }

  return (
    <Modal
      open={isUserDeletionConsentNeeded(userDeletionConsent)}
      dismissButton={false}
      disableBackdropClick
      size="initial"
      backdrop="shading"
      onMotionHiddenEnd={() => {
        setHasConfirmed(true);
      }}
    >
      <Modal.Header>{t('common:consent.title')}</Modal.Header>
      <Form
        onSubmit={(event: SubmitEvent<HTMLFormElement>) =>
          handleSubmit(onSubmit)(event)
        }
      >
        <PText>{t('common:consent.userDeletion.description')}</PText>

        <Spacer mb="lg" />

        <Controller
          name="agree"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <PCheckbox
              label={t('common:consent.userDeletion.agree')}
              aria-label={t('common:consent.userDeletion.agree')}
              onChange={(event) => {
                const { checked } = event.target as HTMLInputElement;
                field.onChange(checked);
              }}
            />
          )}
        />

        <ActionGroup>
          <div />

          <PButton
            type="submit"
            icon="arrow-right"
            disabled={!formState.isValid}
            loading={isPending}
          >
            {t('common:consent.userDeletion.goTo', { app: t('common:title') })}
          </PButton>
        </ActionGroup>
      </Form>
    </Modal>
  );
};
